import {
  type ChangeEvent,
  type MouseEvent,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { EllipsisVerticalIcon } from 'lucide-react';

import type { BuyerLeadDnDColumnFragment } from '@graphql/__generated__/graphql';
import {
  getEffectiveLot,
  getEffectiveUser,
} from '@modules/_shared/components/buyer-lead/buyerLeadMlsFallback';
import { BuyerLeadNextStep } from '@modules/_shared/components/buyer-lead/BuyerLeadNextStep';
import { getBuyerLeadOutcomeContext } from '@modules/_shared/components/buyer-lead/buyerLeadOutcomeContext';
import { useBuyerLeadLostWorkflow } from '@modules/_shared/components/feed/hooks/useBuyerLeadLostWorkflow';
import { useKanbanSelectedState } from '@modules/_shared/components/kanban/KanbanBoard';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserCard } from '@modules/_shared/components/UserCard';
import { getStageCardColors } from '@modules/_shared/utils/leadStageSelectHelpers';
import { useLocale } from '@shared/hooks/locale';

export const BuyerLeadItemMenu = memo<{
  anchorEl: HTMLElement | null;
  onCloseMenu: () => void;
  onDelete?: (leadId: string) => void;
  leadStatus: string | null | undefined;
  leadId: string;
}>(({ anchorEl, onCloseMenu, onDelete, leadStatus, leadId }) => {
  const { t } = useLocale();
  const open = Boolean(anchorEl);
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);
  const { lostReasonDialog, triggerActive, triggerWon, triggerLost } =
    useBuyerLeadLostWorkflow({ setErrorLike });
  const outcomeContext = getBuyerLeadOutcomeContext(leadId);

  const handleCloseMenu = (event: Event | MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    onCloseMenu();
  };

  return (
    <>
      {lostReasonDialog}
      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />
      <Menu
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        slotProps={{
          root: {
            'data-no-dnd': true,
          } as any,
        }}
      >
        {(leadStatus === 'won' || leadStatus === 'lost') && (
          <MenuItem
            onClick={event => {
              void triggerActive(outcomeContext);
              handleCloseMenu(event);
            }}
          >
            {t('Mark as active')}
          </MenuItem>
        )}
        {leadStatus !== 'won' && (
          <MenuItem
            onClick={event => {
              void triggerWon(outcomeContext);
              handleCloseMenu(event);
            }}
          >
            {t('Mark as won')}
          </MenuItem>
        )}
        {leadStatus !== 'lost' && (
          <MenuItem
            onClick={event => {
              void triggerLost(outcomeContext);
              handleCloseMenu(event);
            }}
          >
            {t('Mark as lost')}
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={event => {
              onDelete(leadId);
              handleCloseMenu(event);
            }}
          >
            {t('Delete')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
});

export const BuyerLeadKanbanItem = memo(
  ({
    lead,
    onDelete,
    includeLotReference,
    readOnly = false,
  }: {
    lead: BuyerLeadDnDColumnFragment;
    onDelete?: (leadId: string) => void;
    includeLotReference?: boolean;
    readOnly?: boolean;
  }) => {
    const theme = useTheme();
    const palette = (theme.vars ?? theme).palette;
    const { selectedItems, setSelectedItems, forceSelectionMode } =
      useKanbanSelectedState();
    const background = useMemo(
      () => getStageCardColors(lead.lead_stage?.status, palette).background,
      [lead.lead_stage?.status, palette],
    );

    const selected = useMemo(
      () => selectedItems?.includes(lead.id) ?? false,
      [selectedItems, lead.id],
    );
    const selectionInProgress = useMemo(
      () =>
        forceSelectionMode ||
        (selectedItems != null && selectedItems.length > 0),
      [selectedItems, forceSelectionMode],
    );
    const memoizedOnSelect = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (setSelectedItems == null || selectedItems == null) {
          return;
        }

        if (event.target.checked) {
          setSelectedItems([...new Set([...selectedItems, lead.id])]);
        } else {
          setSelectedItems(selectedItems.filter(id => id !== lead.id));
        }
      },
      [lead.id, selectedItems, setSelectedItems],
    );

    return (
      <BuyerLeadKanbanItemContent
        onSelect={memoizedOnSelect}
        selectionInProgress={selectionInProgress}
        selected={selected}
        background={background}
        lead={lead}
        onDelete={onDelete}
        includeLotReference={includeLotReference}
        readOnly={readOnly}
      />
    );
  },
);

const BuyerLeadKanbanItemContent = memo<{
  background: string;
  lead: BuyerLeadDnDColumnFragment;
  selectionInProgress: boolean;
  selected: boolean;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onDelete?: (leadId: string) => void;
  includeLotReference?: boolean;
  readOnly?: boolean;
}>(
  ({
    background,
    lead,
    selectionInProgress,
    selected,
    onSelect,
    onDelete,
    includeLotReference,
    readOnly = false,
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { t } = useLocale();
    const effectiveUser = useMemo(() => getEffectiveUser(lead), [lead]);
    const effectiveLot = useMemo(() => getEffectiveLot(lead), [lead]);
    const secondaryText = useMemo(() => {
      return (
        <>
          {lead.source != null && `${lead.source} · `}
          <TimeAgo dateString={lead.created_at} tooltipPlacement="bottom" />
          {includeLotReference && effectiveLot?.lot_reference != null && (
            <>
              <br />
              <Typography variant="caption">
                {t('Listing {{reference}}', {
                  reference: effectiveLot.lot_reference,
                })}
              </Typography>
            </>
          )}
          {lead.user != null && (
            <BuyerLeadNextStep activity={lead.next_activity[0]} />
          )}
        </>
      );
    }, [
      lead.source,
      lead.created_at,
      lead.user,
      lead.next_activity,
      includeLotReference,
      effectiveLot?.lot_reference,
      t,
    ]);

    const handleOpenMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(event.currentTarget);
      },
      [setAnchorEl],
    );
    const userActions = useMemo(
      () =>
        readOnly ? null : (
          <IconButton edge="end" onClick={handleOpenMenu} data-no-dnd>
            <EllipsisVerticalIcon size={20} />
          </IconButton>
        ),
      [handleOpenMenu, readOnly],
    );
    const onCloseMenu = useCallback(() => {
      setAnchorEl(null);
    }, [setAnchorEl]);

    return (
      <Box
        sx={{
          p: 1.5,
          background,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {effectiveUser != null && (
          <UserCard
            user={effectiveUser}
            condensed
            forceCheckbox={readOnly ? false : selectionInProgress}
            selected={selected}
            onSelect={readOnly ? undefined : onSelect}
            secondaryText={secondaryText}
            userActions={userActions}
            alignItems="flex-start"
          />
        )}
        <BuyerLeadItemMenu
          anchorEl={anchorEl}
          leadStatus={lead.lead_stage?.status}
          leadId={lead.id}
          onCloseMenu={onCloseMenu}
          onDelete={onDelete}
        />
      </Box>
    );
  },
);
